import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { UserSettingKey } from 'services/player';

export interface SettingOption {
  value: string;
  label: string;
  // Shorter label for the Settings list row; falls back to `label`.
  shortLabel?: string;
}

export interface SettingDefinition {
  key: UserSettingKey;
  title: string;
  description: string;
  // Opening Casino's casino options are loaded at runtime and appended after these.
  // Compare Insights has none: it uses CompareInsightsPicker instead of a list.
  options: SettingOption[];
}

const TIMEFRAME_OPTIONS: SettingOption[] = [
  { value: 'months1', label: '30 days', shortLabel: '30 Days' },
  { value: 'weeks1', label: '7 days', shortLabel: '7 Days' },
  { value: 'hours24', label: '24 hours', shortLabel: '24 Hours' },
  { value: 'hours8', label: '8 hours', shortLabel: '8 Hours' },
  { value: 'hours4', label: '4 hours', shortLabel: '4 Hours' },
  { value: 'hours1', label: '1 hour', shortLabel: '1 Hour' },
];

// Same stats and labels as the Compare view's insight picker, in the design's order.
export const COMPARE_INSIGHT_OPTIONS: SettingOption[] = [
  { value: InsightFilterEnum.ReturnToPlayer, label: 'RTP' },
  { value: InsightFilterEnum.Jackpot, label: 'Jackpots' },
  { value: InsightFilterEnum.MoneyPlayed, label: '$ Played' },
  { value: InsightFilterEnum.MoneyWon, label: '$ Won' },
  { value: InsightFilterEnum.Spins, label: 'Spins' },
  { value: InsightFilterEnum.AverageWonPerSpin, label: '$ W/S' },
  { value: InsightFilterEnum.JackpotWonPerSpin, label: 'J W/S' },
  { value: InsightFilterEnum.NonJackpotWonPerSpin, label: 'W/S-J' },
];

export const MAX_COMPARE_INSIGHTS = 3;

// Mirrors the server default in services/ms-player/src/API/userSettings.ts.
export const DEFAULT_COMPARE_INSIGHTS: string[] = [
  InsightFilterEnum.ReturnToPlayer,
  InsightFilterEnum.Jackpot,
  InsightFilterEnum.MoneyPlayed,
];

// Older records (and older servers) store a single string such as 'default';
// anything that isn't a valid list of compare stats falls back to the default.
export const toCompareInsights = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [...DEFAULT_COMPARE_INSIGHTS];
  const known = COMPARE_INSIGHT_OPTIONS.map((option) => option.value);
  const insights = (value as unknown[])
    .filter(
      (id, index, all): id is string =>
        typeof id === 'string' && known.includes(id) && all.indexOf(id) === index
    )
    .slice(0, MAX_COMPARE_INSIGHTS);
  return insights.length ? insights : [...DEFAULT_COMPARE_INSIGHTS];
};

export const CASINO_OPTION_PREFIX = 'casino:';

type AppDefaultSettingKey = Extract<
  UserSettingKey,
  | 'openingCasino'
  | 'openingPage'
  | 'exploreInsight'
  | 'exploreTimeframe'
  | 'compareInsights'
  | 'compareTimeframe'
  | 'drawerState'
>;

export const SETTING_DEFINITIONS: Record<AppDefaultSettingKey, SettingDefinition> = {
  openingCasino: {
    key: 'openingCasino',
    title: 'Opening Casino',
    description: 'Choose what casino the app starts on by default.',
    options: [
      { value: 'chooseEachTime', label: 'Choose each time', shortLabel: 'Choose' },
      { value: 'mostRecent', label: 'Most Recent' },
      { value: 'mostVisited', label: 'Most Visited' },
    ],
  },
  openingPage: {
    key: 'openingPage',
    title: 'Opening Page',
    description: 'Choose what page the app starts on by default.',
    options: [
      { value: 'login', label: 'Login' },
      { value: 'casinos', label: 'Choose a casino' },
      { value: 'explore', label: 'Explore' },
      { value: 'myPlay', label: 'MyPlay' },
      { value: 'events', label: 'Events' },
      { value: 'leaders', label: 'Leaders' },
    ],
  },
  exploreInsight: {
    key: 'exploreInsight',
    title: 'Explore Insight',
    description: 'Choose what insight to start on when opening the explore page.',
    options: [
      { value: 'mostRecent', label: 'Most Recent' },
      { value: 'hottest', label: 'Hottest Slots' },
      { value: 'coldest', label: 'Coldest Slots' },
      { value: 'jackpots', label: 'Jackpots' },
      { value: 'moneyWon', label: '$ Won' },
      { value: 'moneyPlayed', label: '$ Played' },
      { value: 'popular', label: 'Popular' },
      { value: 'jackpotWinPerSpin', label: 'JW/S' },
      { value: 'winPerSpinWithoutJackpots', label: 'W/S - J' },
      { value: 'playedSinceLastJackpot', label: '$ Played SLJ' },
    ],
  },
  exploreTimeframe: {
    key: 'exploreTimeframe',
    title: 'Explore Timeframe',
    description: 'Choose what timeframe to start on when opening the explore page.',
    options: TIMEFRAME_OPTIONS,
  },
  compareInsights: {
    key: 'compareInsights',
    title: 'Compare Insights',
    description: 'Choose what Insight(s) to start on when opening the compare page.',
    options: [],
  },
  compareTimeframe: {
    key: 'compareTimeframe',
    title: 'Compare Timeframe',
    description: 'Choose what timeframe to start on when opening the compare page.',
    options: TIMEFRAME_OPTIONS,
  },
  drawerState: {
    key: 'drawerState',
    title: 'Drawer State',
    description: 'Choose what state the drawer defaults to.',
    options: [
      { value: 'full', label: 'Fully Opened', shortLabel: 'Full Open' },
      { value: 'closed', label: 'Fully Closed', shortLabel: 'Closed' },
      { value: 'half', label: 'Partially Open', shortLabel: 'Half Open' },
    ],
  },
};

export type OptionSettingKey = keyof typeof SETTING_DEFINITIONS;

// Order of the App Defaults rows on the Settings page.
export const APP_DEFAULT_KEYS: OptionSettingKey[] = [
  'openingCasino',
  'openingPage',
  'exploreInsight',
  'exploreTimeframe',
  'compareInsights',
  'compareTimeframe',
  'drawerState',
];

export const isOptionSettingKey = (key: string): key is OptionSettingKey =>
  key in SETTING_DEFINITIONS;

// Route for one App Defaults page. The key is limited to real settings because
// IonRouterOutlet uses the last matching route, and a bare `:settingKey` would
// also match /settings/notifications.
export const SETTING_OPTIONS_PATH = `/settings/:settingKey(${APP_DEFAULT_KEYS.join('|')})`;

// Settings values are scalars, ordered string arrays (Compare Insights) or
// flat objects of flags (notification groups).
export const isSameSettingValue = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const left = a as Record<string, unknown>;
    const right = b as Record<string, unknown>;
    const keys = Object.keys({ ...left, ...right });
    return keys.every((key) => left[key] === right[key]);
  }
  return a === b;
};
